import { useMemo, useState } from 'react'
import { router } from '@inertiajs/react'
import { AlertTriangle } from 'lucide-react'
import { EmptyState } from '@/frontend/components/EmptyState'
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert'
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { undoableAction } from '@/frontend/components/undo/undoableAction'
import type { CronData, CronJob, RoutineExecution } from '@/frontend/pages/Dashboard/DigitalWorkers/types'
import RotinasJobList from './rotinas/RotinasJobList'
import RotinasJobsTable from './rotinas/RotinasJobsTable'
import RotinaEditDialog from './rotinas/RotinaEditDialog'

interface Props {
  crons: CronData | null
  // Execuções REAIS das rotinas (Iteração 3, Onda C) — substitui a projeção
  // fabricada. Deferida com os crons; ausente enquanto carrega.
  routineExecutions?: RoutineExecution[]
  basePath?: string
  canManage?: boolean
}

export default function RotinasTab({ crons, routineExecutions, basePath, canManage = false }: Props) {
  const [editJob, setEditJob] = useState<CronJob | null>(null)
  const [deleteJob, setDeleteJob] = useState<CronJob | null>(null)
  // Pausar/reativar otimista (SZ-1589): overlay local de `enabled` por job
  // enquanto o commit adiado do undoableAction ainda não confirmou no servidor.
  const [enabledOverrides, setEnabledOverrides] = useState<Record<string, boolean>>({})

  // All hooks above early returns to satisfy Rules of Hooks
  const jobs = useMemo(() => {
    const raw = crons?.jobs ?? []
    return raw.map((job) => {
      const override = enabledOverrides[job.id]
      return override === undefined || override === job.enabled ? job : { ...job, enabled: override }
    })
  }, [crons, enabledOverrides])
  // Execuções reais das rotinas do agente. O match primário é o id estável do
  // cron; nome só cobre telemetria antiga sem cron_job_id.
  const executions = useMemo(() => {
    const ids = new Set(jobs.map((job) => job.id))
    const names = new Set(jobs.map((job) => job.name))
    return (routineExecutions ?? []).filter((execution) => {
      if (execution.routine_id) return ids.has(execution.routine_id)
      return Boolean(execution.routine_name && names.has(execution.routine_name))
    })
  }, [routineExecutions, jobs])
  const status = crons?.status
  const showActions = Boolean(canManage && basePath)

  const deleteSelectedJob = () => {
    if (!deleteJob || !basePath) return

    router.delete(`${basePath}/crons/${encodeURIComponent(deleteJob.id)}`, {
      preserveScroll: true,
      onSuccess: () => {
        setDeleteJob(null)
        router.reload({ only: ['crons'] })
      },
    })
  }

  const clearOverride = (jobId: string) => {
    setEnabledOverrides((overrides) => {
      const rest = { ...overrides }
      delete rest[jobId]
      return rest
    })
  }

  // Pausa/reativa com undo de 5s (SZ-1589): a UI muda na hora e o PATCH só sai
  // quando o toast expira; "Desfazer" restaura o estado sem nenhum request.
  const toggleJobEnabled = (job: CronJob) => {
    if (!basePath) return
    const next = !job.enabled
    const previous = enabledOverrides[job.id]

    undoableAction({
      message: next ? `Rotina "${job.name}" reativada` : `Rotina "${job.name}" pausada`,
      apply: () => setEnabledOverrides((overrides) => ({ ...overrides, [job.id]: next })),
      revert: () => setEnabledOverrides((overrides) => {
        const rest = { ...overrides }
        if (previous === undefined) delete rest[job.id]
        else rest[job.id] = previous
        return rest
      }),
      commit: () => {
        router.patch(`${basePath}/crons/${encodeURIComponent(job.id)}`, { cron: { enabled: next } }, {
          preserveScroll: true,
          onSuccess: () => router.reload({
            only: ['crons'],
            // Prop atualizado volta a ser a fonte de verdade; manter o override
            // mascararia um eventual erro do Fleet no toggle.
            onFinish: () => clearOverride(job.id),
          }),
        })
      },
    })
  }

  // Estados vazios do DS: uma frase só, média — sem caixa.
  if (!crons) {
    return <p className="py-6 text-[13px] text-muted-foreground">Não foi possível carregar as rotinas.</p>
  }

  if (crons.error) {
    return <p className="py-6 text-[13px] text-muted-foreground">{crons.error}</p>
  }

  if (jobs.length === 0) {
    return (
      <EmptyState
        illustration="routines"
        title="Nada no piloto automático ainda"
        description="Peça uma rotina no chat e o resto roda no automático."
      />
    )
  }

  return (
    <div className="space-y-6">
      {status && status.jobsInError > 0 && (
        <Alert variant="warning">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>
            {status.jobsInError === 1
              ? '1 rotina apresentou erro na última execução'
              : `${status.jobsInError} rotinas apresentaram erro na última execução`}
          </AlertTitle>
          <AlertDescription>
            As rotinas com erro estão marcadas em vermelho na lista abaixo.
          </AlertDescription>
        </Alert>
      )}

      {/* DS novo: sem Card pesado (sombra + px-6). Seção leve — heading 14/650
          + contagem tabular sobre a mesma caixa clara de Publicações
          (rounded-xl border, sem sombra), pra as abas de Trabalho combinarem. */}
      <section className="space-y-2.5">
        <h2 className="flex items-baseline gap-2 text-[14px] font-semibold text-foreground">
          Rotinas configuradas
          <span className="text-[12px] font-normal tabular-nums text-faint-foreground">
            {jobs.length}
          </span>
        </h2>
        <div className="overflow-x-auto rounded-xl border border-border bg-card">
          <RotinasJobsTable
            jobs={jobs}
            canManage={showActions}
            onEdit={setEditJob}
            onDelete={setDeleteJob}
            onToggleEnabled={toggleJobEnabled}
          />
        </div>
      </section>

      {/* Últimas execuções — REAIS (fleet_sessions channel=cron), não mais
          fabricadas do schedule. */}
      <section className="space-y-2.5">
        <h2 className="flex items-baseline gap-2 text-[14px] font-semibold text-foreground">
          Últimas execuções
          <span className="text-[12px] font-normal tabular-nums text-faint-foreground">
            {executions.length}
          </span>
        </h2>
        <div className="overflow-x-auto rounded-xl border border-border bg-card">
          <RotinasJobList executions={executions} />
        </div>
      </section>

      {basePath && (
        <RotinaEditDialog
          job={editJob}
          open={editJob !== null}
          basePath={basePath}
          onOpenChange={(open) => { if (!open) setEditJob(null) }}
        />
      )}

      <AlertDialog open={deleteJob !== null} onOpenChange={(open) => { if (!open) setDeleteJob(null) }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir rotina</AlertDialogTitle>
            <AlertDialogDescription>
              A rotina "{deleteJob?.name}" será removida do OpenClaw e não executará novamente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-white hover:bg-destructive/90"
              onClick={deleteSelectedJob}
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
